import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, forkJoin, of } from 'rxjs';
import { Store } from '@ngxs/store';
import { distinctUntilChanged, map, skipWhile, switchMap, take } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MenuState } from '@nimbus-library/features/menu/state/menu.state';
import { MenuActions } from '@nimbus-library/features/menu/state/menu.actions';
import { MenuEntryViewModel } from '@nimbus-library/features/menu/models/menu-entry.view-model';
import { IPermissionManagerService } from '@nimbus-library/features/user-settings/services/contracts/i-permission-manager.service';
import { UiProfilePortalState } from '@nimbus-portal/features/ui-profile/store/ui-profile-portal.state';

@UntilDestroy()
@Component({
  standalone: false,
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingPageComponent implements OnInit {
  // Honour the configured default page at most once per app load. If the target is unreachable
  // (blocked by a feature/permission guard that redirects back to `/`), the next pass falls through
  // to the menu-based navigation instead of looping between `/` and the default page.
  private static hasHonouredDefaultPage = false;

  public constructor(
    private readonly router: Router,
    private readonly store: Store,
    private readonly permissionManagerService: IPermissionManagerService,
  ) {}

  public ngOnInit(): void {
    combineLatest([
      this.permissionManagerService.getPermissionsModel$(),
      this.store.select(MenuState.menu).pipe(distinctUntilChanged(isEqual)),
      this.store.select(UiProfilePortalState.loaded).pipe(distinctUntilChanged(isEqual)),
    ])
      .pipe(
        skipWhile(([permissionModel, menu, profileLoaded]) => !menu || !permissionModel || !profileLoaded),
        take(1),
        switchMap(([, menu]) => {
          const defaultPagePath = LandingPageComponent.hasHonouredDefaultPage
            ? null
            : this.store.selectSnapshot(UiProfilePortalState.defaultPageRoute);
          if (defaultPagePath) {
            LandingPageComponent.hasHonouredDefaultPage = true;
            return of(defaultPagePath);
          }
          return forkJoin(
            ...menu.entries.map((m) =>
              m.filter$.pipe(
                take(1),
                map((result) => (result ? m : null)),
              ),
            ),
          ).pipe(map((menuItems: Array<MenuEntryViewModel | null>) => menuItems.find((m) => !!m) ?? null));
        }),
        untilDestroyed(this),
      )
      .subscribe((target: string | MenuEntryViewModel | null) => {
        if (typeof target === 'string') {
          this.router.navigateByUrl(target);
        } else if (target) {
          this.store.dispatch(new MenuActions.Navigate(target));
        }
      });
  }
}
